
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { countries } from "@/data/countries";
import { ProfileData } from "@/types/profile";

const formSchema = z.object({
  full_name: z.string().min(1, { message: "Full name is required" }),
  nickname: z.string().min(1, { message: "Nickname is required" }),
  age: z.number().min(1, { message: "Age is required" }),
  gender: z.string().min(1, { message: "Gender is required" }),
  country: z.string().min(1, { message: "Country is required" }),
});

interface ProfileFormProps {
  initialData?: ProfileData;
  isSignup?: boolean;
}

const ProfileForm: React.FC<ProfileFormProps> = ({ initialData, isSignup = false }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      full_name: initialData?.full_name || "",
      nickname: initialData?.nickname || "",
      age: initialData?.age ? (typeof initialData.age === 'string' ? parseInt(initialData.age) : initialData.age) : undefined,
      gender: initialData?.gender || "",
      country: initialData?.country || "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("No user found");
      }

      console.log("Submitting profile data:", values);
      console.log("User ID:", user.id);

      // 使用 upsert 來避免重複插入的問題
      const { data, error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          full_name: values.full_name,
          nickname: values.nickname,
          age: values.age,
          gender: values.gender,
          country: values.country,
          is_verified: true // 明確設置驗證狀態
        }, {
          onConflict: 'id'
        })
        .select()
        .single();

      console.log("Profile upsert result:", { data, error });

      if (error) {
        console.error("Profile upsert error:", error);
        throw error;
      }

      toast({
        title: language === 'en' ? "Profile completed!" : "個人資料完成！",
        description: language === 'en' 
          ? "Your profile has been successfully updated." 
          : "您的個人資料已成功更新。",
      });

      // 確保導航到正確的頁面
      if (isSignup) {
        console.log("Signup completed, navigating to home");
        navigate('/', { replace: true });
      } else {
        console.log("Profile updated, navigating to profile page");
        navigate('/profile', { replace: true });
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        variant: "destructive",
        title: language === 'en' ? "Error" : "錯誤",
        description: error.message || (language === 'en' 
          ? "Failed to update profile. Please try again." 
          : "更新個人資料失敗，請重試。"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>
          {language === 'en' ? 'Complete Your Profile' : '完成您的個人資料'}
        </CardTitle>
        <CardDescription>
          {language === 'en' 
            ? 'Please fill in your information to complete registration' 
            : '請填寫您的資訊以完成註冊'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="full_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {language === 'en' ? 'Full Name' : '真實姓名'}
                    <span className="text-red-500 ml-1">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder={language === 'en' ? "Enter your full name" : "請輸入您的真實姓名"} 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nickname"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {language === 'en' ? 'Nickname' : '暱稱'}
                    <span className="text-red-500 ml-1">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder={language === 'en' ? "Enter your nickname" : "請輸入您的暱稱"} 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="age"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {language === 'en' ? 'Age' : '年齡'}
                    <span className="text-red-500 ml-1">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      placeholder={language === 'en' ? "Enter your age" : "請輸入您的年齡"} 
                      {...field}
                      value={field.value || ""} 
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(value === "" ? undefined : parseInt(value) || undefined);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="gender"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {language === 'en' ? 'Gender' : '性別'}
                    <span className="text-red-500 ml-1">*</span>
                  </FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={language === 'en' ? "Select gender" : "請選擇性別"} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="male">
                        {language === 'en' ? 'Male (男)' : '男'}
                      </SelectItem>
                      <SelectItem value="female">
                        {language === 'en' ? 'Female (女)' : '女'}
                      </SelectItem>
                      <SelectItem value="other">
                        {language === 'en' ? 'Other (其他)' : '其他'}
                      </SelectItem>
                      <SelectItem value="prefer_not_to_say">
                        {language === 'en' ? 'Prefer not to say (不願透露)' : '不願透露'}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {language === 'en' ? 'Country/Region' : '國家/地區'}
                    <span className="text-red-500 ml-1">*</span>
                  </FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={language === 'en' ? "Select country" : "請選擇國家"} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {isSignup 
                            ? (language === 'en' ? `${country.name_en} (${country.name_zh})` : country.name_zh)
                            : country.name_zh
                          }
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading 
                ? (language === 'en' ? 'Updating...' : '更新中...') 
                : (isSignup 
                  ? (language === 'en' ? 'Complete Registration' : '完成註冊')
                  : (language === 'en' ? 'Update Profile' : '完成更新')
                )
              }
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default ProfileForm;
